import { formatToHumanReadable } from "@/lib/format-data";

// Firebase collapses "wrong password" and "no such account" into a single
// credential error (email-enumeration protection), so we can't tell them apart.
// Map them to one message that names both possibilities without revealing which
// — and, for the humanized fallback below, avoids leaking a raw "User Not Found".
const CREDENTIAL_ERROR_CODES = new Set([
  "auth/invalid-credential",
  "auth/invalid-login-credentials",
  "auth/wrong-password",
  "auth/user-not-found",
]);

const TOO_MANY_ATTEMPTS_MESSAGE =
  "Too many attempts. Please wait and try again later.";

const CREDENTIAL_ERROR_MESSAGE =
  "Incorrect email or password, or you don't have an account yet.";

// Accounts created with Google have no password, and Firebase reports a
// password attempt on one as the same credential error.
const GOOGLE_ACCOUNT_HINT =
  "If you signed up with Google, use Sign in with Google instead.";

export function isCredentialError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" && CREDENTIAL_ERROR_CODES.has(code);
}

export function normalizeAuthError(
  error: unknown,
  { offersGoogle = false }: { offersGoogle?: boolean } = {},
): string {
  if (error && typeof error === "object") {
    const code = (error as { code?: unknown }).code;
    if (isCredentialError(error)) {
      return offersGoogle
        ? `${CREDENTIAL_ERROR_MESSAGE} ${GOOGLE_ACCOUNT_HINT}`
        : CREDENTIAL_ERROR_MESSAGE;
    }
    const message = (error as { message?: unknown }).message;
    if (
      code === "auth/too-many-requests" ||
      code === "TOO_MANY_ATTEMPTS_TRY_LATER" ||
      (typeof message === "string" &&
        message.includes("TOO_MANY_ATTEMPTS_TRY_LATER"))
    ) {
      return TOO_MANY_ATTEMPTS_MESSAGE;
    }
    if (typeof message === "string") {
      return formatToHumanReadable(
        message
          .replace("Firebase: ", "")
          .replace("Error (", "")
          .replace("auth/", "")
          .replace(").", "")
          .replace(/-/g, " "),
      );
    }
  }
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Something went wrong. Please try again.";
}
